import { commsReaderLink } from '@/components/comms/comms-links'
import {
  type PassageSpeaker,
  type PassageSpeakerDto,
  passageSpeakers,
} from '@/features/humint/recording/passage-speakers'
import { passageSource } from '@/features/humint/recording/recording-path'

/**
 * Pure parsing + shaping helpers for the `hybrid_search` (search_documents)
 * tool result. No React, no IO — the controller (HybridSearchToolUI) and the
 * presentational view both consume these.
 */

export interface HybridSearchArgs {
  query: string
  limit?: number
  /** Search what was said in recordings, and nothing else. */
  recordings_only?: boolean
  /** Search the scenes and the people of the videos, and nothing else (PRD-0074). */
  videos_only?: boolean
  /** Search inside one document: its id, or the id of the graph node that stands for it. */
  document_id?: string | null
  /** Search the recordings of one person: the person's node id. */
  person_id?: string | null
}

/** The chunk type of a passage hit: about a minute of a recording's transcript. */
const PASSAGE_CHUNK_TYPE = 'transcript_passage'

export interface HybridSearchHit {
  chunk_id: string
  document_id: string
  document_name: string
  page?: number | string | null
  score: number
  text: string
  /** Set on conversation-window hits only (ADR-0010). A window is
   * self-anchoring — it knows its thread and its moment — which is what lets a
   * purely semantic hit open the reader instead of dead-ending at a file name. */
  chunk_type?: string
  conversation_id?: string | null
  ts_from?: string | null
  ts_to?: string | null
  /** Set on row-window hits only (structured ingest): the table reference
   * `<analysis>/<table>` and the parquet row numbers the window renders. */
  structured_table?: string | null
  row_indices?: number[] | null
  /** Set on passage hits only (`transcript_passage`): the passage's node in the
   * graph, where it lies in its recording — as the transcript writes time,
   * "12:30", "1:02:30" past the hour — and the voices that speak in it, by
   * their number on the recording page. The hit's `text` is the passage whole,
   * a line per turn. */
  node_id?: string | null
  time_from?: string
  time_to?: string
  start_seconds?: number
  /** Where a scene's or an appearance's window ends, in seconds (PRD-0074 slice 08). */
  end_seconds?: number
  voices?: number[]
  /** A scene hit's key frame and an appearance hit's crop, as `/api/v1/files/…` paths. */
  keyframe_path?: string | null
  crop_path?: string | null
  track_id?: number | null
  /** The HUMINT capture a passage's recording is, whose page plays it at a
   * time; null for an audio file uploaded to the case, absent from a server
   * that does not name it yet. */
  capture_id?: string | null
}

/** What a hit is evidence of, and so where it opens. */
export type HitKind =
  | 'conversation'
  | 'rows'
  | 'passage'
  | 'photo'
  | 'document'
  | 'scene'
  | 'appearance'
  | 'plate'

/** The chunk a photo is found by: the description of what is on it. */
const PHOTO_CHUNK_TYPE = 'image_description'
/** A recording's window, a person seen in it without a face, a plate read in it (PRD-0074). */
const SCENE_CHUNK_TYPE = 'video_scene'
const APPEARANCE_HIT_TYPE = 'person_appearance'
const PLATE_CHUNK_TYPE = 'video_plate'
/** The hits that are a moment of a recording and open the player there. */
const VIDEO_KINDS: ReadonlySet<HitKind> = new Set(['scene', 'appearance', 'plate'])

/** A window is one only while it names what it is a window of; anything else
 * found in a file is a chunk of that document. A photo found by its description
 * opens as the document it is, and is shown as a photo. */
export function hitKind(hit: HybridSearchHit): HitKind {
  if (hit.chunk_type === 'comm_window' && hit.conversation_id) return 'conversation'
  if (hit.chunk_type === 'row_window' && hit.structured_table) return 'rows'
  if (hit.chunk_type === PHOTO_CHUNK_TYPE) return 'photo'
  if (hit.chunk_type === SCENE_CHUNK_TYPE) return 'scene'
  if (hit.chunk_type === APPEARANCE_HIT_TYPE) return 'appearance'
  if (hit.chunk_type === PLATE_CHUNK_TYPE) return 'plate'
  return hit.chunk_type === PASSAGE_CHUNK_TYPE ? 'passage' : 'document'
}

/** A hit that is a moment of a recording: it opens the player there. */
export function isVideoHit(hit: HybridSearchHit): boolean {
  return VIDEO_KINDS.has(hitKind(hit))
}

/** Where a passage, a scene, an appearance or a plate lies in its recording: "12:30 – 13:42".
 * Null for a hit that is none of them, or names no start. */
export function passageSpan(hit: HybridSearchHit): string | null {
  const kind = hitKind(hit)
  if ((kind !== 'passage' && !VIDEO_KINDS.has(kind)) || !hit.time_from) return null
  const ends = hit.time_to && hit.time_to !== hit.time_from
  return ends ? `${hit.time_from} – ${hit.time_to}` : hit.time_from
}

/** `…/scenes/3.jpg` → `…/scenes/3.thumb.jpg`, as the engine names the small copy. */
export function thumbnailPath(picture: string): string {
  return picture.replace(/\.([a-z0-9]+)$/i, '.thumb.$1')
}

/**
 * The picture a video hit shows — a scene's key frame, an appearance's crop — as the small copy
 * the engine writes beside it (`<name>.thumb.jpg`, 160 px): a card lists many, and a key frame
 * is a few hundred kilobytes. Null for the rest.
 */
export function hitPicture(hit: HybridSearchHit): string | null {
  const kind = hitKind(hit)
  const picture =
    kind === 'scene' ? hit.keyframe_path : kind === 'appearance' ? hit.crop_path : null
  return picture ? thumbnailPath(picture) : null
}

/** The voices that speak in a passage, each once. A voice is a number from 1;
 * a turn nobody was recognised in belongs to none. */
export function passageVoices(hit: HybridSearchHit): number[] {
  if (hitKind(hit) !== 'passage') return []
  const voices = (hit.voices ?? []).filter(voice => Number.isInteger(voice) && voice > 0)
  return [...new Set(voices)]
}

/** One voice of a passage hit, and who it was said to be on the recording's
 * page; null for a voice nobody named, which stays a number. */
export interface HitVoice {
  voice: number
  speaker: PassageSpeaker | null
}

/** The voices that speak in a passage hit, each with who the recording's page
 * says it is. `legend` is the answer's `speakers`: only the entries of the
 * hit's own recording count, so one recording never lends another its names. */
export function passageHitVoices(hit: HybridSearchHit, legend: unknown): HitVoice[] {
  const voices = passageVoices(hit)
  if (voices.length === 0) return []
  const speakers = passageSpeakers(legend, hit.document_id)
  return voices.map(voice => ({ voice, speaker: speakers.get(voice) ?? null }))
}

/** Where a passage of a HUMINT recording is heard: the recording's page, at
 * the second the passage starts in (the page finds the turn inside it). Null
 * for any other hit — an uploaded audio file has no page that takes a time,
 * and opens as the document it is. `passageSource` decides which, as it does
 * for a passage on the graph. */
export function recordingLink(caseId: string, hit: HybridSearchHit): string | null {
  if (hitKind(hit) !== 'passage') return null
  const source = passageSource(caseId, {
    captureId: hit.capture_id,
    documentId: hit.document_id,
    atSeconds: hit.start_seconds,
  })
  return source?.kind === 'recording' ? source.to : null
}

/** The ways a hit can open, as the connected card supplies them. Each is
 * optional: a hit nothing can open is shown as static text. */
export interface HitWays {
  /** Open a hit's source document. Needs the hit's `document_id`. */
  onOpenDocument?: (documentId: string) => void
  /** A video hit opens its recording at the moment it was seen (slice 08); without this, as a document. */
  onOpenAt?: (hit: HybridSearchHit) => void
  /** Conversation-window hits open the Mobile Forensics reader instead of the
   * document overlay — a window's evidence is the thread, not a file. */
  onOpenConversation?: (hit: HybridSearchHit) => void
  /** Row-window hits (structured ingest) open the exact source rows of the
   * table — the evidence is the rows, verbatim, not a rendered text. */
  onOpenRows?: (hit: HybridSearchHit) => void
  /** Where a passage hit of a HUMINT recording is heard (`recordingLink`):
   * an address, so the row is a link that also opens beside the chat. A
   * passage it answers null for opens as the document it is. */
  recordingHref?: (hit: HybridSearchHit) => string | null
}

/** How one hit opens: an address to follow, or an action to run. */
export type HitWay = { to: string } | { run: () => void }

/** How a hit opens with the ways at hand; null when nothing can open it. A
 * conversation opens its reader and a row window its rows. A passage of a
 * HUMINT recording leads to the recording's page; any other passage was found
 * in a file like a page of a document, and opens as that document. */
export function hitWay(hit: HybridSearchHit, ways: HitWays): HitWay | null {
  const { onOpenDocument, onOpenConversation, onOpenRows, onOpenAt, recordingHref } = ways
  const kind = hitKind(hit)
  if (kind === 'conversation') {
    return onOpenConversation ? { run: () => onOpenConversation(hit) } : null
  }
  if (kind === 'rows') return onOpenRows ? { run: () => onOpenRows(hit) } : null
  if (VIDEO_KINDS.has(kind) && onOpenAt && hit.document_id && hit.start_seconds != null) {
    return { run: () => onOpenAt(hit) }
  }
  const to = kind === 'passage' ? recordingHref?.(hit) : null
  if (to) return { to }
  return onOpenDocument && hit.document_id ? { run: () => onOpenDocument(hit.document_id) } : null
}

/** Where a conversation-window hit should take the analyst: the Mobile
 * Forensics reader, opened on that thread at the window's start. */
export function conversationLink(caseId: string, hit: HybridSearchHit): string | null {
  if (hit.chunk_type !== 'comm_window' || !hit.conversation_id) return null
  // Anchor on the window's end: the reader loads the messages up to the anchor
  // and nothing newer, so anchoring on the start would leave the window's own
  // content below the edge of the page.
  return commsReaderLink({
    caseId,
    conversationId: hit.conversation_id,
    from: hit.ts_to ?? hit.ts_from,
  })
}

export interface RowWindowTarget {
  analysisId: string
  tableId: string
  positions: number[]
}

/** What a row-window hit points at: the structured table and the exact rows
 * behind the window, ready for the rows drilldown. */
export function rowWindowTarget(hit: HybridSearchHit): RowWindowTarget | null {
  if (hit.chunk_type !== 'row_window' || !hit.structured_table) return null
  const [analysisId, tableId] = hit.structured_table.split('/')
  if (!analysisId || !tableId) return null
  return { analysisId, tableId, positions: hit.row_indices ?? [] }
}

/** How a search was scoped, as its answer repeats it. */
export interface HybridSearchScope {
  recordings_only?: boolean
  videos_only?: boolean
  /** The person whose recordings were searched. */
  person?: { node_id: string; name: string } | null
  /** The documents searched; absent for the whole case. */
  document_ids?: string[]
  /** False: the scope was applied to the case's nearest passages only, so a
   * miss there is not proof. */
  exhaustive?: boolean
}

export interface HybridSearchResult {
  /** `no_recordings` (the person has no recording) and `no_text` (nothing is
   * indexed under the document): nothing was searched, and `note` says why. */
  status: 'ok' | 'empty' | 'no_recordings' | 'no_text' | 'error'
  query: string
  count?: number
  hits?: HybridSearchHit[]
  /** The voices somebody named on a recording's page, of the recordings the
   * passage hits were found in: each entry names its recording by
   * `document_id`. Absent when no hit's recording has a named voice. The
   * lines of a hit's `text` already name the same speakers, in the tool's words. */
  speakers?: PassageSpeakerDto[]
  scope?: HybridSearchScope
  /** The tool's own words to the model: why nothing was searched, or where to
   * look after an empty search. */
  note?: string
  error?: string
  duration_ms?: number
}

/** Whether the search had nothing to look in: an answer of its own, not an
 * empty search. */
export function nothingSearched(result: HybridSearchResult | null): boolean {
  return result?.status === 'no_recordings' || result?.status === 'no_text'
}

/** Where a scoped search looked. */
export type SearchedIn =
  | { kind: 'person'; name: string }
  | { kind: 'documents'; count: number }
  | { kind: 'recordings' }
  | { kind: 'videos' }

/** Where a scoped search looked, by what its scope names first: a person's
 * recordings, else the documents, else recordings only, else videos only. Null
 * for a search of the whole case. */
export function searchedIn(scope: HybridSearchScope | null | undefined): SearchedIn | null {
  if (!scope) return null
  if (scope.person?.name) return { kind: 'person', name: scope.person.name }
  if (scope.document_ids?.length) return { kind: 'documents', count: scope.document_ids.length }
  if (scope.recordings_only) return { kind: 'recordings' }
  return scope.videos_only ? { kind: 'videos' } : null
}

export function parseResult(
  raw: HybridSearchResult | string | undefined
): HybridSearchResult | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as HybridSearchResult
    } catch {
      return { status: 'error', query: '', error: raw }
    }
  }
  return raw
}

export function scoreColor(score: number): string {
  if (score >= 0.7) return 'bg-success/20 text-success-foreground'
  if (score >= 0.4) return 'bg-accent text-accent-foreground'
  return 'bg-muted text-muted-foreground'
}
